import { randomItem, randomShuffle, Sprite, XY } from '../../../odie';
import { app } from '../../app';
import { config } from '../../defs/config';
import { BlockEntity, CoolerBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { getMatch2AssetPath } from '../../util/assets';
import { blockIterateAll } from '../../util/blockTools';
import { getCollisionTracker } from '../../util/CollisionTracker';
import { mapToViewPosition } from '../../util/mapTools';
import { IEffect } from './IEffect';
import gsap from 'gsap';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import { BreakEffect } from './BreakEffect';
import { waitForItPromise } from 'src/lib/utils';

gsap.registerPlugin(MotionPathPlugin);

/**
 * Number of soda rockets that the cooler box can launch
 */
export const COOLER_BOX_SODA_ROCKETS = 6;

export class CoolerEffect implements IEffect {
  private readonly scene: GameScene;
  private readonly cooler: CoolerBlockEntity;
  private readonly collisionTracker = getCollisionTracker();

  constructor(scene: GameScene, cooler: CoolerBlockEntity) {
    this.scene = scene;
    this.cooler = cooler;
  }

  public async execute() {
    // Find out possible targets for the soda rockets, limited to the number of rockets it can launch
    const targets = this.getRocketTargets(COOLER_BOX_SODA_ROCKETS);
    const promises = [];

    this.coolerView.sodas = targets.length;

    // Launch soda rockets at each target
    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];
      const delay = i * 300; // Delay between each rocket launch, in milliseconds
      promises.push(this.launchSodaRocket(target, delay));
    }

    // Await all animation promises to finish
    await Promise.all(promises);

    // Play break animation at the end when removing the cooler
    new BreakEffect(this.scene, {
      position: this.cooler.c.position.mapPosition,
      size: this.cooler.c.block.props,
      color: 0x699df3,
    }).execute();
  }

  private getRocketTargets(maxTargets: number) {
    const targetsByPriority: XY[][] = [];

    // Find out all possible targets
    blockIterateAll(this.scene, (base, overlay) => {
      const priority = this.getTargetPriority(base, overlay);
      if (priority === -1) return;
      if (!targetsByPriority[priority]) {
        targetsByPriority[priority] = [];
      }
      targetsByPriority[priority].push(base.c.position.mapPosition);
    });

    // Reverse the array so that the highest priority is first
    targetsByPriority.reverse();

    // Shuffle all possible targets
    for (let i = 0; i < targetsByPriority.length; i++) {
      if (targetsByPriority[i]) {
        randomShuffle(targetsByPriority[i]);
      }
    }

    // Select 3 random targets
    const selectedTargets = targetsByPriority.flat();
    return selectedTargets.slice(0, maxTargets);
  }

  private getTargetPriority(base?: BlockEntity, overlay?: BlockEntity) {
    // Invalid if base is undefined or the cooler itself
    if (!base || base === this.cooler) return -1;

    // Some overlay blocks like curtains and walls are not valid targets
    if (overlay) {
      const type = overlay.c.block.props.type;
      switch (type) {
        case 'wall':
          return -1;
        case 'curtain':
          return -1;
      }
    }

    // Already triggered cooler blocks are not valid
    if (base.c.blockCooler?.triggered) return -1;

    // Basic blocks without overlay are the lowest priority
    if (!overlay && base.c.block.props.type === 'basic') return 1;

    // Other cooler blocks without overlay are 4th in priority
    if (!overlay && base.c.block.props.type === 'cooler') return 2;

    // Any blocks with an overlay (other than cooler) are 3rd in priority
    if (overlay && base.c.block.props.type !== 'cooler') return 3;

    // Indestructible blocks are 2nd in priority, but they have special rules
    if (base.c.block.props.immortal) {
      const priority = 4;
      const type = base.c.block.props.type;
      const session = this.scene.sessionEntity.c.state;
      const goals = this.scene.sessionEntity.c.map.goals;
      switch (type) {
        case 'thrower':
          // Only valid if there are throws remaining
          return session.cageThrowsRemaining ? priority : -1;
        case 'parcels':
          // Only valid if there are throws remaining
          return session.parcelThrowsRemaining ? priority : -1;
        case 'pipe':
          // Only valid if there are pipe (chicken/egg) goals remaining
          return goals['pipe'] ? priority : -1;
        default:
          return -1;
      }
    }

    // Valid non-basic blocks are the highest priority
    return 5;
  }

  private async launchSodaRocket(target: XY, delayMs: number) {
    // Wait for the delay before launching, so the rocket can be created in the correct position
    // since the cooler box can fall down with gravity while launching the rockets.
    if (delayMs > 0) await waitForItPromise(delayMs);

    const particles = this.createSodaParticles();
    const rocket = this.createSodaRocket();
    const duration = 1;
    const from = { x: rocket.x, y: rocket.y };
    const to = this.getViewPosition(target);
    const lastPosition = { ...from };

    // Scale rocket up and down during the launch animation
    gsap.to(rocket, {
      scaleX: 0.7,
      scaleY: 0.7,
      duration: duration * 0.5,
      yoyo: true,
      repeat: 1,
      ease: 'sine.out',
    });

    // Animate rocket through a path
    await gsap.to(rocket, {
      motionPath: {
        type: 'soft',
        path: [
          { x: from.x + randomItem([-50, 50]), y: to.y - 300 },
          { x: to.x, y: to.y },
        ],
        curviness: 2,
      },
      duration,
      ease: 'linear',
      onStart: () => {
        app.sound.play('puzzle-rocket-activate', { volume: 0.4, dupes: 2 });
        this.coolerView.sodas -= 1;
        rocket.visible = true;
        particles.spawnX = rocket.x;
        particles.spawnY = rocket.y;
        particles.start().then(() => particles.view.removeFromParent());
      },
      onUpdate: () => {
        rocket.rotation = this.getRotation(lastPosition, rocket);
        lastPosition.x = rocket.x;
        lastPosition.y = rocket.y;
        particles.spawnX = rocket.x;
        particles.spawnY = rocket.y;
      },
    });

    // Execute attack collision at target, then remove the rocket
    app.sound.play('puzzle-hat-destroy', { volume: 0.4, dupes: 2 });
    const map = this.scene.sessionEntity.c.map;
    this.collisionTracker.collideAt(map, target, 'attack', {
      type: 'rocket',
    });
    rocket.removeFromParent();
    particles.stop();

    // Play hit animation at the rocket target
    new BreakEffect(this.scene, {
      position: target,
      size: { width: 1, height: 1 },
      color: 0x93d0f9,
    }).execute();
  }

  private createSodaRocket() {
    const viewPosition = this.getViewPosition(
      this.cooler.c.position.mapPosition,
    );
    const rocket = Sprite.from('cooler/block.cooler.soda.png');
    rocket.x = viewPosition.x;
    rocket.y = viewPosition.y;
    rocket.anchorX = rocket.anchorY = 0.5;
    rocket.scaleX = rocket.scaleY = 0.3;
    rocket.visible = false;
    this.scene.stage.addChild(rocket);
    return rocket;
  }

  private createSodaParticles() {
    const emitter = app.particles.create({
      textures: [
        getMatch2AssetPath('fx.rocket/fx.rocket.smoke1.png'),
        getMatch2AssetPath('fx.rocket/fx.rocket.smoke2.png'),
        getMatch2AssetPath('fx.rocket/fx.rocket.smoke3.png'),
        getMatch2AssetPath('fx.rocket/fx.rocket.smoke4.png'),
      ],
      rate: 0.025,
      limit: 64,
      duration: 1,
      scale: [
        { x: 0.5, y: 0.5 },
        { x: 1, y: 1 },
      ],
      behaviors: [
        { type: 'explode', magnitude: [16, 32] },
        { type: 'scale', to: 0 },
        { type: 'tint', from: 0xff53d2, to: 0xffffff },
        { type: 'kinematic' },
      ],
    });
    this.scene.stage.addChild(emitter.view);
    return emitter;
  }

  private getViewPosition(target: XY) {
    const viewPosition = mapToViewPosition(target);
    return {
      x: viewPosition.x + config.tile.size * 0.5,
      y: viewPosition.y + config.tile.size * 0.5,
    };
  }

  private getRotation(from: XY, to: XY) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    return -Math.atan2(dy, dx) - Math.PI * 0.5;
  }

  private get coolerView() {
    return this.cooler.c.blockCooler.view;
  }
}
